import * as React from 'react'
import { closeHelpCenterModal } from '../../store/modals/actions'
import ModalHeader from './Parts/ModalHeader'
import { AppState } from '../../store'
import { AnyAction, Dispatch } from 'redux'
import { connect } from 'react-redux'
import ModalWindow from './Parts/ModalWindow'
import ModalMiddle from './Parts/ModalMiddle'
import ModalContent from './Parts/ModalContent'
import { useTranslation } from 'react-i18next'
import ModalLoader from './Parts/ModalLoader'
import Button from '../Button/Button'
import { HelpCentersController } from '../../controllers'
import { CurrentUser, DisplayableError, HelpCenter, Project } from '../../types'
import TooltipError from '../Tooltips/ErrorTooltip'
import CheckboxInput from '../Form/CheckboxInput'

export type HelpCenterModalTab = 'details' | 'notes' | 'planning'

interface IStateToProps {
  currentUser: CurrentUser
  helpCenter?: HelpCenter
  onSubmit: (helpCenter: HelpCenter) => void
}

interface IDispatchToProps {
  close: typeof closeHelpCenterModal
}

type IProps = IDispatchToProps & IStateToProps

interface IState {
  didInitialLoad: boolean
  helpCenter: HelpCenter
  errors: DisplayableError[]
}

const HelpCenterModal = (props: IProps) => {
  const { t } = useTranslation()
  const { currentUser } = props
  const [state, setState] = React.useState<IState>({
    didInitialLoad: false,
    helpCenter: null,
    errors: [],
  })
  const { helpCenter, errors, didInitialLoad } = state

  React.useEffect(() => {
    getForm().catch(console.error)
  }, [])

  const getForm = async () => {
    try {
      const { help_center } = await HelpCentersController.getForm({ id: props.helpCenter ? props.helpCenter.id : null })

      setState({
        ...state,
        helpCenter: {
          ...help_center,
          ...props.helpCenter,
        },
        didInitialLoad: true,
      })
    } catch (ex) {
      console.error(ex)
    }
  }


  const onNameChange = (e) => {
    setState({ ...state, helpCenter: { ...helpCenter, name: e.currentTarget.value } })
  }

  const onEnabledChange = () => {
    setState({ ...state, helpCenter: { ...helpCenter, enabled: !helpCenter.enabled } })
  }

  const onCloseClick = () => {
    props.close()
  }

  const onFormSubmit = async (e) => {
    e.preventDefault()

    try {
      let response = null
      if (helpCenter.id) {
        response = await HelpCentersController.update(helpCenter)
      } else {
        response = await HelpCentersController.create(helpCenter)
      }

      if (response.errors) {
        setState({ ...state, errors: response.errors })
      } else {
        if (props.onSubmit) props.onSubmit(response)
        props.close()
      }

    } catch (ex) {
      console.error(ex)
    }
  }

  const onErrorsDismiss = () => {
    setState({ ...state, errors: [] })
  }

  const renderDetails = () => {
    return (
      <div className='grid'>
        <div className='grid-cell with-6col'>
          <div className='form-item'>
            <label>{t('HelpCenterModal::Name')} <span>*</span></label>
            <input
              type='text'
              placeholder={t('HelpCenterModal::Name')}
              onChange={onNameChange}
              value={helpCenter.name}
            />
          </div>
        </div>

        <div className='grid-cell with-6col'>
          <div className='form-item'>
            <label>{t('HelpCenterModal::Enabled')}</label>
            <CheckboxInput
              checked={helpCenter.enabled}
              label={t('HelpCenterModal::Enabled')}
              onChange={onEnabledChange}
            />
          </div>
        </div>
      </div>
    )
  }

  return (
    <ModalWindow>
      <ModalHeader
        title={helpCenter?.id ? t('HelpCenterModal::Update help center') : t('HelpCenterModal::Create help center')}
        onCloseClick={onCloseClick}
      />

      {!didInitialLoad && <ModalLoader />}
      {didInitialLoad && <ModalMiddle>
        <ModalContent>
          <form onSubmit={onFormSubmit}>
            {renderDetails()}
            <input type='submit' style={{ display: 'none' }} onClick={onFormSubmit} />
          </form>
        </ModalContent>

        <div className='modal-footer'>
          <div />
          <div key='main-action' className='popover-wrapper'>
            <TooltipError
              errors={errors}
              onDismiss={onErrorsDismiss}
            />
            <Button
              type='success'
              text={helpCenter?.id ? t('HelpCenterModal::Update') : t('HelpCenterModal::Create')}
              onClick={onFormSubmit}
            />
          </div>
        </div>
      </ModalMiddle>}
    </ModalWindow >
  )
}

const mapStateToProps = (state: AppState): IStateToProps => {
  const {
    authentication: {
      currentUser
    },
    modals: {
      helpCenterModal: {
        helpCenter,
        onSubmit,
      }
    }
  } = state

  return {
    currentUser: currentUser,
    helpCenter: helpCenter,
    onSubmit: onSubmit,
  }
}

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>): IDispatchToProps => {
  return {
    close: () => dispatch(closeHelpCenterModal()),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(HelpCenterModal)